import React, { useState } from 'react';
import { ArrowUpRight, ChevronDown } from 'lucide-react';
import {
  DRAPERU_PROGRAMS_URL,
  INTRO,
  INTRO_FUND,
  PROGRAMS,
} from '../data/acceleration';
import { inCohort } from '../data/portfolio';

// Order Apex as listed by the program team.
const APEX_ORDER = ['Kenomic', 'ChatterPay', 'Ascent Rivals', 'Origami Tech', 'Mentioned', 'Nomadz', 'Ospree', 'Masumi'];
const COHORTS = [
  { key: 'genesis-1', name: 'GENESIS', companies: inCohort('genesis-1') },
  {
    key: 'apex-1',
    name: 'APEX',
    companies: inCohort('apex-1').sort((a, b) => APEX_ORDER.indexOf(a.name) - APEX_ORDER.indexOf(b.name)),
  },
];

const initialsOf = (n) => n.split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

function GenesisTile({ c }) {
  const inner = (
    <>
      <div className={`of-genesis-logo ${c.logoBg ? `is-${c.logoBg}` : ''}`}>
        {c.logo ? <img src={c.logo} alt={`${c.name} logo`} loading="lazy" decoding="async" className={c.avatar ? 'is-avatar' : undefined} /> : <span>{initialsOf(c.name)}</span>}
      </div>
      <div className="of-genesis-name">{c.name}</div>
      <div className="of-genesis-sector">{c.sector}</div>
    </>
  );
  return c.url ? (
    <a href={c.url} target="_blank" rel="noopener noreferrer" className="of-genesis-tile">{inner}</a>
  ) : (
    <div className="of-genesis-tile">{inner}</div>
  );
}
// Parse "July 13th 2026" style strings
const parseDate = (s) => new Date(s.replace(/(\d+)(st|nd|rd|th)/, '$1'));

function programStatus(p) {
  if (p.closed) return { label: 'Cohort Closed', live: false };
  const now = new Date();
  const due = parseDate(p.dates[0][1]);
  const start = parseDate(p.dates[2][1]);
  if (now <= due) return { label: 'Applications open', live: true };
  if (now < start) return { label: `Starts ${p.dates[2][1].replace(/ 20\d\d$/, '')}`, live: true };
  return { label: 'Cohort Closed', live: false };
}

function ProgramCard({ p, index, open, onToggle }) {
  const status = programStatus(p);
  return (
    <article className="of-panel of-reveal flex flex-col">
      <div className="p-6 md:p-8 border-b border-[color:var(--of-line)]">
        <div className="flex items-start justify-between gap-4">
          <span className="of-eyebrow">{String(index + 1).padStart(2, '0')} — {p.duration}</span>
          <span className="of-tag">
            <span className={`w-1.5 h-1.5 rounded-full ${status.live ? 'bg-[#3ecf6a]' : 'bg-[color:var(--of-muted)]'}`} />
            {status.label}
          </span>
        </div>
        <h3 className="mt-6 text-3xl md:text-4xl tracking-[-0.03em] text-white">
          {p.name} <span className="text-[color:var(--of-muted)]">| {p.kind}</span>
        </h3>
        <p className="mt-1 text-sm text-[color:var(--of-accent-2)]">{p.audience}</p>
        <p className="mt-5 text-sm leading-relaxed text-[color:var(--of-ink-dim)] md:min-h-[6.5em]">{p.summary}</p>
      </div>

      {/* Details toggle: terms, dates and what's included (both cards open together) */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={`group w-full px-6 md:px-8 py-5 flex items-center justify-between gap-4 text-left text-[15px] text-white hover:text-[color:var(--of-accent-2)] ${open ? 'border-b border-[color:var(--of-line)]' : ''}`}
      >
        <span>Details</span>
        <span className="flex items-center gap-2 of-mono text-[11px] uppercase tracking-[0.08em] text-[color:var(--of-muted)] group-hover:text-white">
          {open ? 'Collapse' : 'Expand'}
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {open && (
      <>
      {/* Key terms */}
      <div className="grid grid-cols-3 border-b border-[color:var(--of-line)]">
        {[
          ['Investment', p.investment],
          ['Equity', p.equity.replace(' target equity', '')],
          ['Cohort', p.cohort],
        ].map(([k, v], i) => (
          <div key={k} className={`p-5 md:p-6 ${i < 2 ? 'border-r border-[color:var(--of-line)]' : ''}`}>
            <div className="of-stat-label">{k}</div>
            <div className="of-stat-value mt-2 text-lg md:text-2xl">{v}</div>
          </div>
        ))}
      </div>

      {/* Dates */}
      <dl className="p-6 md:p-8 border-b border-[color:var(--of-line)] space-y-3">
        {p.dates.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between gap-4 text-sm">
            <dt className="text-[color:var(--of-muted)]">{k}</dt>
            <dd className="flex-1 mx-3 border-b border-dashed border-[color:var(--of-line-strong)] translate-y-[-3px]" />
            <dd className="of-mono text-white">{v}</dd>
          </div>
        ))}
      </dl>

      {/* What's included */}
      <div className="flex-1">
        <div className="px-6 md:px-8 pt-7 pb-4"><span className="of-eyebrow">What's included</span></div>
        <ul className="px-6 md:px-8 pb-6 md:pb-8 pt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
          {p.benefits.map(([t, d]) => (
            <li key={t}>
              <div className="flex items-center gap-2 text-[15px] text-white">
                <span className="w-1 h-1 bg-[color:var(--of-accent)]" />
                {t}
              </div>
              <p className="mt-1 pl-3 text-[13px] leading-relaxed text-[color:var(--of-muted)]">{d}</p>
            </li>
          ))}
        </ul>
      </div>
      </>
      )}
    </article>
  );
}

/** Acceleration (Draper University x Cardano) section of the one-page landing. Anchor: #acceleration */
export default function AccelerationSection() {
  const [detailsOpen, setDetailsOpen] = useState(false);
  return (
    <div id="acceleration" className="of-anchor">
        {/* Section head */}
        <section className="of-hero of-rails">
          <div className="of-frame pt-24 md:pt-32 pb-12 md:pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <span className="of-eyebrow of-reveal">
                <span className="of-dot" /> Acceleration
              </span>
              <h2 className="of-display of-reveal mt-6 text-[2.6rem] sm:text-6xl lg:text-7xl">
                Cardano <span className="text-[color:var(--of-muted)]">×</span> <strong>DraperU</strong> Accelerator Programs
              </h2>
              <p className="of-lede of-reveal mt-6">
                Join Draper University in Silicon Valley and build your blockchain startup on Cardano.
              </p>
            </div>
            <div className="lg:col-span-4 of-reveal flex lg:justify-end items-center gap-5">
              <img src="/logos/draper-university.svg" alt="Draper University" className="of-logo-tint h-12 w-auto" />
              <span className="h-8 w-px bg-[color:var(--of-line-strong)]" />
              <img src="/logos/cardano-foundation-white.svg" alt="Cardano Foundation" className="of-logo-tint h-9 w-auto" />
            </div>
          </div>
        </section>

        {/* Intro */}
        <section className="of-rails">
          <div className="of-frame py-14 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-3">
              <span className="of-eyebrow of-reveal">00 — The programs</span>
            </div>
            <div className="lg:col-span-9">
              <p className="of-reveal text-xl md:text-[1.75rem] leading-snug tracking-[-0.02em] text-white">{INTRO}</p>
              <p className="of-reveal mt-6 text-[15px] leading-[1.75] text-[color:var(--of-ink-dim)] max-w-3xl">
                {INTRO_FUND}
              </p>
              <div className="of-reveal mt-8 flex flex-wrap items-center gap-3">
                <a href={DRAPERU_PROGRAMS_URL} target="_blank" rel="noopener noreferrer" className="of-btn px-5 h-11 min-w-[11.5rem]">
                  Visit Draper University <ArrowUpRight className="of-arrow w-4 h-4" />
                </a>
                <a href="https://x.com/draper_u" target="_blank" rel="noopener noreferrer" className="of-btn-ghost px-5 h-11 min-w-[11.5rem]" aria-label="Draper University on X">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                  @draper_u
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Programs */}
        <section className="of-rails">
          <div className="of-frame pb-16 md:pb-24 grid grid-cols-1 xl:grid-cols-2 gap-4">
            {PROGRAMS.map((p, i) => (
              <ProgramCard key={p.key} p={p} index={i} open={detailsOpen} onToggle={() => setDetailsOpen((o) => !o)} />
            ))}
          </div>
        </section>

        {/* Logos — light band */}
        <section className="of-light of-rails">
          <div className="of-frame py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-3">
              <span className="of-eyebrow of-reveal">01 — Past Cardano startups accelerated</span>
            </div>
            <div className="lg:col-span-9 space-y-12 md:space-y-16">
              {COHORTS.map((co) => (
                <div key={co.key} className="of-reveal">
                  <div className="of-genesis-head">
                    <span className="of-team-tag"><b>{co.name}</b> COHORT 1 · 2026</span>
                    <span className="of-genesis-count">{co.companies.length} companies</span>
                  </div>
                  <div className="of-genesis-grid">
                    {co.companies.map((c) => (
                      <GenesisTile key={c.name} c={c} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

    </div>
  );
}
